import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, ArrowRight, ShoppingBag, Mail, Check } from "lucide-react";
import { useCart } from "@/shop/CartContext";
import { fmtMoney } from "@/shop/shopApi";
import { easeOutExpo } from "@/shop/motion";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CartDrawer() {
  const {
    cart, open, setOpen, updateItem, busy,
    email, setEmail, name, setName, beginCheckout,
  } = useCart();

  const [localEmail, setLocalEmail] = useState(email);
  const [localName, setLocalName] = useState(name);
  const [saved, setSaved] = useState(!!email);

  useEffect(() => { setLocalEmail(email); setSaved(!!email); }, [email]);
  useEffect(() => { setLocalName(name); }, [name]);

  const emailValid = EMAIL_RE.test(localEmail);

  const saveEmail = (e) => {
    e?.preventDefault?.();
    if (!emailValid) return;
    setEmail(localEmail.trim());
    setName((localName || "").trim());
    setSaved(true);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            className="fixed top-0 right-0 bottom-0 z-[61] w-full max-w-md bg-[#0e0e10] border-l border-white/10 flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: easeOutExpo }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
              <p className="font-display font-semibold tracking-tight">Your Cart {cart?.total_quantity ? `(${cart.total_quantity})` : ""}</p>
              <button onClick={() => setOpen(false)} className="p-2 text-white/60 hover:text-white" aria-label="Close cart" data-testid="cart-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {(!cart || !cart.lines?.length) ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-white/50 gap-4">
                  <ShoppingBag className="w-10 h-10" strokeWidth={1} />
                  <p>Your cart is empty.</p>
                </div>
              ) : (
                <ul className="divide-y divide-white/10">
                  {cart.lines.map((l) => (
                    <li key={l.line_id} className="py-5 flex gap-4" data-testid={`cart-line-${l.line_id}`}>
                      <div className="w-20 h-20 bg-white/5 rounded-lg overflow-hidden shrink-0">
                        {l.image && <img src={l.image} alt={l.product_title} className="w-full h-full object-cover" loading="lazy" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm leading-tight truncate">{l.product_title}</p>
                        {l.variant_title && l.variant_title !== "Default Title" && (
                          <p className="text-xs text-white/50 mt-0.5">{l.variant_title}</p>
                        )}
                        <p className="text-sm mt-1 sh-blue">{fmtMoney(l.price)}</p>
                        <div className="mt-3 inline-flex items-center border border-white/15 rounded-full">
                          <button onClick={() => updateItem(l.line_id, l.quantity - 1)} disabled={busy} className="p-1.5 text-white/70 hover:text-white" aria-label="Decrease">
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="px-3 text-sm tabular-nums">{l.quantity}</span>
                          <button onClick={() => updateItem(l.line_id, l.quantity + 1)} disabled={busy} className="p-1.5 text-white/70 hover:text-white" aria-label="Increase">
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {cart?.lines?.length > 0 && (
              <div className="border-t border-white/10 px-6 py-5 space-y-4">
                {/* Email capture for cart recovery */}
                <form onSubmit={saveEmail} className="space-y-2" data-testid="cart-recovery-form">
                  <label className="text-[10px] uppercase tracking-[0.18em] text-white/50 flex items-center gap-1.5">
                    <Mail className="w-3 h-3" strokeWidth={1.6} />
                    Save cart & get updates
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={localEmail}
                      onChange={(e) => { setLocalEmail(e.target.value); setSaved(false); }}
                      placeholder="you@email.com"
                      className="flex-1 bg-white/[0.04] border border-white/10 focus:border-sh-blue outline-none rounded-lg px-3 h-10 text-sm placeholder-white/30"
                      data-testid="cart-recovery-email"
                      autoComplete="email"
                    />
                    <button
                      type="submit"
                      disabled={!emailValid || saved}
                      className={`h-10 px-4 rounded-lg text-xs font-medium transition-colors ${
                        saved
                          ? "bg-green-500/20 text-green-300 border border-green-500/30"
                          : emailValid
                            ? "bg-white text-black hover:bg-white/90"
                            : "bg-white/10 text-white/40 cursor-not-allowed"
                      }`}
                      data-testid="cart-recovery-save"
                    >
                      {saved ? <Check className="w-4 h-4" /> : "Save"}
                    </button>
                  </div>
                  {!saved && (
                    <p className="text-[11px] text-white/40 leading-relaxed">
                      We'll hold your cart & send a friendly nudge if you don't finish.
                    </p>
                  )}
                  {saved && (
                    <p className="text-[11px] text-green-400/80 leading-relaxed" data-testid="cart-recovery-saved">
                      Cart saved to <span className="font-mono">{email}</span> — we'll email you if you leave items behind.
                    </p>
                  )}
                </form>

                <div className="flex items-center justify-between text-sm pt-2 border-t border-white/10">
                  <span className="text-white/60">Subtotal</span>
                  <span className="font-semibold">{fmtMoney(cart.subtotal || cart.total)}</span>
                </div>
                <p className="text-xs text-white/40">Shipping & taxes calculated at checkout.</p>
                <button
                  onClick={beginCheckout}
                  data-testid="cart-checkout"
                  className="w-full h-14 rounded-full bg-sh-blue text-white font-medium flex items-center justify-center gap-2 hover:bg-[#3b9bff] transition-colors"
                >
                  Checkout <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-center text-[11px] text-white/40">Secure checkout powered by Shopify</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
